/**
 * @file ImageElementPanel.tsx
 * @description Панель «Фото» (phase19.16 Р2; uiux-design-r2 §4, §10;
 * SB66, SB97): медиатека, режим [Обычное][Заполнить колонку][Вырезанный
 * объект], пропорции числом 0,2–6 (ширина ÷ высота, пределы схемы; SB120,
 * `NumberSliderField`; 1:1 · 4:3 · 3:4 · 16:9 · 3:2 — подсказки, «↺ как у
 * файла» — нет ключа), точка фокуса, «Что на фото» (иначе предупреждение),
 * до 2 надписей поверх, напоминание о согласии людей на фото.
 *
 * SB120 этап 1 (uiux-sb120-stage1 §3 и «Дополнение» §3): режим fit | bleed
 * (прежнее «Заполнить колонку» — высота «По колонке»), кадр для ПК и
 * телефона (`ImageFrameFields`), высота «По фото / По колонке / Числом»
 * (`ImageHeightField`); шаг «Кадр под блок» (`ImageFrameStep`) — сразу после
 * выбора фото и по «Кадр…»: рамка по доле колонки × высоте.
 *
 * SB120 этап 3а (uiux-sb120-stage3 §3): надпись на фото — `OverlayCard`
 * (стиль, место x/y по кадру, шрифт, размер, цвет, плашка с контрастом по
 * худшему случаю, «Скрыть на телефоне»).
 */

import { SECTION_TEXT_LIMITS, SITE_LIMITS, defaultSectionOverlay, type SectionElementOf } from "@chaiberi/shared";
import { Crop } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { MediaIdField } from "../fields/MediaIdField";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { ImageFrameFields } from "./ImageFrameFields";
import { ImageFrameStep } from "./ImageFrameStep";
import { ImageHeightField } from "./ImageHeightField";
import { useSectionForm } from "../section-form-context";
import { OverlayCard } from "./OverlayCard";
import { overlayPlaceName } from "../../../../lib/site-builder/element-place";
import type { ElementPanelProps } from "./types";

type Overlay = SectionElementOf<"image">["overlays"][number];

export const NO_ALT_TEXT = "Опишите фото в двух словах — это нужно поиску и незрячим покупателям.";

export function ImageElementPanel({ element, onChange, path, fresh, onFreshDone, columnWidth = 1280 }: ElementPanelProps<"image">) {
  const [framing, setFraming] = useState(false);
  const { hasIssuesUnder, elementWarnings } = useSectionForm();
  // ✕ у строки надписи: замечание проверки или удалённый свой шрифт.
  const overlayBad = (i: number) => {
    const at = `${path}.overlays.${i}`;
    return hasIssuesUnder(at) || elementWarnings.some((w) => w.code === "element_font_missing" && w.path.startsWith(`${at}.`));
  };
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  return (
    <>
      <MediaIdField
        label="Фото"
        value={element.mediaId}
        openOnMount={fresh}
        onOpened={onFreshDone}
        onChange={(mediaId) => {
          set({ mediaId });
          // Новое фото — сразу кадр под колонку («Пропустить» есть всегда).
          if (mediaId && mediaId !== element.mediaId) setFraming(true);
        }}
        hint="JPEG, PNG или WebP. Для вырезанного объекта — PNG с прозрачным фоном."
      />
      {element.mediaId ? (
        <Button variant="outline" className="self-start" onClick={() => setFraming(true)}>
          <Crop aria-hidden />
          Кадр…
        </Button>
      ) : null}
      <ImageFrameStep open={framing} element={element} columnWidth={columnWidth} onChange={onChange} onClose={() => setFraming(false)} />
      <ImageFrameFields element={element} path={path} onChange={onChange} />
      <ImageHeightField element={element} path={path} onChange={onChange} />
      <SectionTextField label="Что на фото" path={`${path}.alt`} value={element.alt} max={SECTION_TEXT_LIMITS.alt} placeholder="Например: иван-чай в льняном мешочке" onChange={(alt) => set({ alt: alt || undefined })} />
      {element.mediaId && !element.alt?.trim() ? <p className="type-caption text-warning">{NO_ALT_TEXT}</p> : null}
      <SortableList<Overlay>
        label="Надписи на фото"
        items={element.overlays}
        invalidAt={overlayBad}
        max={SITE_LIMITS.sectionOverlays}
        addLabel="Надпись поверх"
        makeItem={() => defaultSectionOverlay("Надпись")}
        onChange={(overlays) => set({ overlays })}
        summary={(o) => `${o.text || "без текста"} · ${overlayPlaceName(o)}`}
        renderItem={(o, update, i) => <OverlayCard overlay={o} onChange={update} path={`${path}.overlays.${i}`} />}
      />
      <p className="type-caption text-on-surface-variant">Если на фото узнаваемы люди, нужно их согласие на публикацию.</p>
    </>
  );
}
